import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Brain, Clock, MapPin, Target, Gift, TrendingUp, Sparkles, RefreshCw, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const CATEGORY_CONFIG = {
  timing: { icon: Clock, color: "bg-blue-100 text-blue-700", accent: "border-blue-200" },
  location: { icon: MapPin, color: "bg-emerald-100 text-emerald-700", accent: "border-emerald-200" },
  strategy: { icon: Target, color: "bg-violet-100 text-violet-700", accent: "border-violet-200" },
  service: { icon: TrendingUp, color: "bg-amber-100 text-amber-700", accent: "border-amber-200" },
  bonus: { icon: Gift, color: "bg-rose-100 text-rose-700", accent: "border-rose-200" },
};

const EarningInsightsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/professional/earning-suggestions`, { withCredentials: true });
      setData(res.data);
      if (isRefresh) toast.success("Insights refreshed!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load insights"));
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { fetchInsights(); }, []);

  const summary = data?.data_summary;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="earning-insights-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2">
                <Brain className="w-6 h-6 text-violet-500" /> AI Earning Insights
              </h1>
              <p className="text-sm text-stone-500 mt-1">Personalized tips to maximize your earnings</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => fetchInsights(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </div>

          {/* Platform Snapshot */}
          {summary && (
            <div className="bg-gradient-to-r from-emerald-950 to-emerald-800 rounded-2xl p-5 mb-6 text-white" data-testid="platform-snapshot">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-5 h-5 text-lime-400" />
                <h3 className="font-semibold text-sm">Platform Snapshot</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-bold text-lime-400">{summary.total_bookings}</p>
                  <p className="text-xs text-emerald-300">Total Bookings</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">
                    {summary.peak_hours?.[0]?.hour !== undefined ? `${summary.peak_hours[0].hour}:00` : "--"}
                  </p>
                  <p className="text-xs text-emerald-300">Peak Hour</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">
                    {summary.peak_days?.[0]?.day || "--"}
                  </p>
                  <p className="text-xs text-emerald-300">Busiest Day</p>
                </div>
              </div>
              {summary.top_services?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-700">
                  <p className="text-xs text-emerald-300 mb-2">Top Services</p>
                  <div className="flex gap-2 flex-wrap">
                    {summary.top_services.map((s) => (
                      <Badge key={s.name} className="bg-emerald-700/50 text-emerald-100 capitalize text-xs">
                        {s.name} ({s.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Tips */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-stone-100 p-5 animate-pulse">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-stone-200 rounded-xl shrink-0" />
                    <div className="flex-1">
                      <div className="h-4 bg-stone-200 rounded w-2/3 mb-2" />
                      <div className="h-3 bg-stone-100 rounded w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="tips-list">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h3 className="font-semibold text-emerald-950 text-sm">AI-Powered Suggestions</h3>
              </div>
              {data?.tips?.map((tip, idx) => {
                const config = CATEGORY_CONFIG[tip.category] || CATEGORY_CONFIG.strategy;
                const Icon = config.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    className={`bg-white rounded-2xl border ${config.accent} p-5`}
                    data-testid={`tip-${idx}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 ${config.color} rounded-xl flex items-center justify-center shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-emerald-950 text-sm">{tip.title}</h4>
                          <Badge variant="outline" className="text-[10px] capitalize px-1.5 py-0">{tip.category}</Badge>
                        </div>
                        <p className="text-sm text-stone-600">{tip.tip}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Footer note */}
          <p className="text-xs text-stone-400 text-center mt-8">
            Suggestions are generated by AI based on platform booking patterns. Results may vary.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default EarningInsightsPage;
